import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Check, ArrowUpRight, TriangleAlert } from 'lucide-react'
import { api, describeError } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import BrandRule from '../components/BrandRule.jsx'
import appLockup from '../assets/crewdispatch-service-logo.png'
import appLockupOnDark from '../assets/crewdispatch-service-logo-on-dark.png'

/**
 * The consent screen: "Claude wants to use CrewDispatch as you, at this company".
 *
 * Deliberately the same card as the sign-in screen. They are seen seconds apart during
 * setup — sign in, then consent — and two different-looking cards asking for the same trust
 * would read as two different products, which is the confusion a phishing page relies on.
 * Same shell, same mark, same width, one more section — and it follows the sign-in screen
 * into dark mode for that reason rather than on its own merits: the pair has to match on a
 * dark phone as exactly as it does on a light desktop.
 *
 * It is the OAuth authorization endpoint, which is why it is a page in this app rather than
 * a server-rendered form. Two things fall out of that for free. Signing in is already
 * solved — App.jsx renders <Login/> in place when there is no session, at this URL, with
 * every query parameter intact — and so is belonging to more than one company: the URL names
 * the company, and if the session is on a different one we move it with the same
 * re-verified switch the account menu uses.
 *
 * Nothing here is decided by the page. Every parameter came from the client through the
 * address bar; the server re-validates all of it on preview AND again on approve, and the
 * code it mints is bound to the session cookie, never to anything sent from here.
 */
export default function Connect() {
  const { search } = useLocation()
  const { switchTenant } = useAuth()
  const [state, setState] = useState({ status: 'loading' })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    api
      .get(`/api/oauth/preview${search}`)
      .then((data) => live && setState({ status: 'ready', data }))
      .catch((err) =>
        live &&
        setState({
          status: 'error',
          // The server says whether this is safe to report by redirecting. A bad client or
          // an unregistered return address is not: that address is exactly what cannot be
          // trusted yet, and bouncing an error to it would make this an open redirector.
          canReturn: Boolean(err.details?.canReturn),
          message: describeError(err, 'That request could not be read.'),
        }),
      )
    return () => {
      live = false
    }
  }, [search])

  /** Everything the client sent, handed back untouched — `state` above all. */
  const params = () => Object.fromEntries(new URLSearchParams(search))

  async function decide(decision) {
    setBusy(true)
    try {
      const res = await api.post('/api/oauth/approve', { ...params(), decision })
      window.location.assign(res.redirectTo)
    } catch (err) {
      // The one refusal the page can act on itself: the session is on another company, so
      // move it and ask again. switchTenant re-verifies membership server-side and reloads.
      if (err.code === 'SWITCH_REQUIRED') {
        setState({ status: 'switching', name: err.details?.target?.name })
        await switchTenant(state.data.target.id).catch(() => {
          setState({ status: 'error', canReturn: true, message: describeError(err) })
        })
        return
      }
      setBusy(false)
      setState((s) => ({ ...s, notice: describeError(err, 'That did not go through.') }))
    }
  }

  const ready = state.status === 'ready'
  return (
    <div className="auth-shell">
      <BrandRule />

      <div className={`auth-card card ${ready ? 'wide' : ''}`}>
        <div className="auth-head">
          {/* Whose site this is, said by the artwork, above a heading that says who is
              asking. The lockup is what a person checks before they trust the page. */}
          <picture className="auth-mark">
            <source srcSet={appLockupOnDark} media="(prefers-color-scheme: dark)" />
            <img src={appLockup} alt="CrewDispatch Service" className="auth-lockup sm" />
          </picture>
          {ready ? (
            <>
              <h1 className="auth-title">{state.data.client.label} wants to connect</h1>
              <p className="auth-tagline">
                as {state.data.user.name}, at {state.data.target.name}
              </p>
            </>
          ) : (
            <>
              <p className="auth-tagline">
                {state.status === 'switching'
                  ? `Switching to ${state.name || 'the right company'}…`
                  : state.status === 'error'
                    ? 'That request could not be completed.'
                    : 'Checking that request…'}
              </p>
            </>
          )}
        </div>

        <div className="auth-body">
          {state.status === 'error' && <ErrorBody state={state} busy={busy} onDecide={decide} />}
          {ready && <Request data={state.data} busy={busy} notice={state.notice} onDecide={decide} />}
        </div>
      </div>
    </div>
  )
}

function ErrorBody({ state, busy, onDecide }) {
  return (
    <>
      <div className="error">{state.message}</div>
      {state.canReturn ? (
        <button className="btn" disabled={busy} onClick={() => onDecide('deny')}>
          Return to the application
        </button>
      ) : (
        <p className="hint auth-centred">
          Close this tab and start again from the application that sent you here.
        </p>
      )}
    </>
  )
}

function Request({ data, busy, notice, onDecide }) {
  const { client, scopes, target, user, sessionMatches } = data
  return (
    <>
      {notice && <div className="error">{notice}</div>}

      <ul className="consent-scopes">
        {scopes.map((s) => (
          <li key={s.scope}>
            <Check size={16} strokeWidth={2.4} />
            <span>{s.text}</span>
          </li>
        ))}
      </ul>

      <dl className="consent-facts">
        <dt>Returns you to</dt>
        <dd className="mono">{client.redirectHost}</dd>
        {/* The name inside a client's own metadata document is self-asserted — anybody can
            host one claiming anything — so it is shown as a claim, beside the host, which is
            the part that cannot be faked. */}
        {client.claimedName && client.claimedName !== client.label && (
          <>
            <dt>Calls itself</dt>
            <dd>{client.claimedName}</dd>
          </>
        )}
        <dt>Acting as</dt>
        <dd>
          {user.name} at {target.name}
        </dd>
      </dl>

      {/* The company on the URL is not the one this session is on. Saying so before the
          click is kinder than a switch happening under them afterwards. */}
      {!sessionMatches && (
        <p className="consent-warn">
          <ArrowUpRight size={15} />
          <span>
            You are signed in to another company. Allowing this switches you to {target.name} first.
          </span>
        </p>
      )}

      {client.loopbackOnly && (
        <p className="consent-warn">
          <TriangleAlert size={15} />
          <span>
            This returns to software running on your own machine. Only allow it if you started
            the connection yourself, just now.
          </span>
        </p>
      )}

      <p className="hint auth-centred">
        It sees and changes only what you can, every change is recorded under your name, and
        you can disconnect it at any time under Settings.
      </p>

      <div className="consent-actions">
        <button className="btn" disabled={busy} onClick={() => onDecide('deny')}>
          Deny
        </button>
        <button className="btn primary" disabled={busy} onClick={() => onDecide('allow')}>
          {busy ? 'Connecting…' : 'Allow'}
        </button>
      </div>
    </>
  )
}
